import React, { useEffect, useState, useMemo, useCallback } from "react";
import DataTable from "react-data-table-component";
import Header from "./Header";
import Footer from "./Footer";
import API_BASE_URL from "../Config";
import "./styles/Specialization.css";
import "../styles/global.css";

/* ────────── SVG Icon System ────────── */
const Icon = ({ d, size = 18, stroke = 1.8 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={stroke}
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ display: "block", flexShrink: 0 }}
  >
    {d}
  </svg>
);

const SearchIcon = (p) => (
  <Icon {...p} d={<><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></>} />
);
const PlusIcon = (p) => (
  <Icon {...p} d={<><path d="M12 5v14M5 12h14" /></>} />
);
const EditIcon = (p) => (
  <Icon {...p} d={<><path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4Z" /></>} />
);
const TrashIcon = (p) => (
  <Icon {...p} d={<><path d="M3 6h18" /><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /><path d="M19 6 18 20a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6M14 11v6" /></>} />
);
const CloseIcon = (p) => (
  <Icon {...p} d={<><path d="M18 6 6 18M6 6l12 12" /></>} />
);
const ChevronLeftIcon = (p) => (
  <Icon {...p} d={<><path d="m15 18-6-6 6-6" /></>} />
);
const ChevronRightIcon = (p) => (
  <Icon {...p} d={<><path d="m9 18 6-6-6-6" /></>} />
);
const CheckIcon = (p) => (
  <Icon {...p} d={<><path d="M20 6 9 17l-5-5" /></>} />
);
const XCircleIcon = (p) => (
  <Icon {...p} d={<><circle cx="12" cy="12" r="10" /><path d="m15 9-6 6M9 9l6 6" /></>} />
);
const ImageIcon = (p) => (
  <Icon {...p} d={<><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><path d="m21 15-5-5L5 21" /></>} />
);
const CheckCircleIcon = (p) => (
  <Icon {...p} d={<><circle cx="12" cy="12" r="10" /><path d="m9 12 2 2 4-4" /></>} />
);
const StarIcon = (p) => (
  <Icon {...p} d={<><path d="M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.123 2.123 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.123 2.123 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.122 2.122 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.122 2.122 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.122 2.122 0 0 0 1.597-1.16z" /></>} />
);

/* ────────── Custom Confirm Dialog ────────── */
const ConfirmDialog = ({ open, title, message, confirmLabel = "Confirm", confirmVariant = "danger", onConfirm, onCancel }) => {
  if (!open) return null;
  return (
    <div className="cdlg-overlay" role="dialog" aria-modal="true" aria-labelledby="cdlg-title">
      <div className="cdlg-box">
        <div className={`cdlg-icon-wrap cdlg-icon-${confirmVariant}`}>
          {confirmVariant === "danger" ? (
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
              <path d="M10 11v6M14 11v6" />
              <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
            </svg>
          ) : (
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          )}
        </div>
        <h3 id="cdlg-title" className="cdlg-title">{title}</h3>
        <p className="cdlg-message">{message}</p>
        <div className="cdlg-actions">
          <button type="button" className="cdlg-btn cdlg-btn-cancel" onClick={onCancel}>
            Cancel
          </button>
          <button type="button" className={`cdlg-btn cdlg-btn-confirm cdlg-btn-${confirmVariant}`} onClick={onConfirm}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ────────── Multi-Select Dropdown ────────── */
const MultiSelect = ({ options, value, onChange, placeholder = "Filter by status…" }) => {
  const [open, setOpen] = useState(false);

  const toggleOption = (opt) => {
    if (value.includes(opt)) {
      onChange(value.filter((v) => v !== opt));
    } else {
      onChange([...value, opt]);
    }
  };

  const clearAll = (e) => {
    e.stopPropagation();
    onChange([]);
  };

  return (
    <div className="bc-multiselect" style={{ position: "relative" }}>
      <div
        className="bc-multiselect__trigger"
        onClick={() => setOpen(!open)}
        tabIndex={0}
        onBlur={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget)) setOpen(false);
        }}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="bc-multiselect__label">
          {value.length === 0
            ? placeholder
            : value.map((v) => (
                <span key={v} className="bc-multiselect__tag">{v}</span>
              ))}
        </span>
        <span className="bc-multiselect__caret">
          {value.length > 0 && (
            <button className="bc-multiselect__clear" onClick={clearAll} tabIndex={-1} type="button">
              <XCircleIcon size={14} />
            </button>
          )}
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)", transition: "transform .2s ease" }}>
            <path d="m6 9 6 6 6-6" />
          </svg>
        </span>
      </div>

      {open && (
        <div className="bc-multiselect__dropdown" role="listbox" aria-multiselectable="true">
          {options.map((opt) => (
            <div
              key={opt}
              className={`bc-multiselect__option ${value.includes(opt) ? "is-selected" : ""}`}
              role="option"
              aria-selected={value.includes(opt)}
              onClick={() => toggleOption(opt)}
              tabIndex={0}
              onKeyDown={(e) => e.key === "Enter" && toggleOption(opt)}
            >
              <span className="bc-multiselect__check">
                {value.includes(opt) && <CheckIcon size={12} stroke={2.5} />}
              </span>
              {opt}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ────────── iPhone-style Toggle ────────── */
const ToggleSwitch = ({ checked, onChange, disabled }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    disabled={disabled}
    onClick={onChange}
    className={`dept-toggle ${checked ? "is-on" : "is-off"}`}
    title={checked ? "Click to deactivate" : "Click to activate"}
  >
    <span className="dept-toggle__thumb" />
  </button>
);

/* ────────── Custom Pagination ────────── */
const CustomPagination = ({ currentPage, totalPages, onPageChange, perPage, totalRows, onPerPageChange }) => {
  const pages = useMemo(() => {
    const arr = [];
    const delta = 2;
    const left = currentPage - delta;
    const right = currentPage + delta + 1;
    let l;
    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || (i >= left && i < right)) arr.push(i);
    }
    const withEllipsis = [];
    arr.forEach((i) => {
      if (l) {
        if (i - l === 2) withEllipsis.push(l + 1);
        else if (i - l > 2) withEllipsis.push("...");
      }
      withEllipsis.push(i);
      l = i;
    });
    return withEllipsis;
  }, [currentPage, totalPages]);

  return (
    <div className="bc-pagination">
      <div className="bc-pagination__info">
        <span>
          Showing <b>{totalRows === 0 ? 0 : Math.min((currentPage - 1) * perPage + 1, totalRows)}</b>–
          <b>{Math.min(currentPage * perPage, totalRows)}</b> of <b>{totalRows}</b>
        </span>
        <select
          className="bc-pagination__perpage"
          value={perPage}
          onChange={(e) => onPerPageChange(Number(e.target.value))}
        >
          {[10, 25, 50, 100].map((n) => (
            <option key={n} value={n}>{n} / page</option>
          ))}
        </select>
      </div>
      <div className="bc-pagination__pages">
        <button
          className="bc-pagination__btn"
          disabled={currentPage === 1}
          onClick={() => onPageChange(currentPage - 1)}
          aria-label="Previous page"
        >
          <ChevronLeftIcon size={16} />
        </button>

        {pages.map((p, i) =>
          p === "..." ? (
            <span key={`ellipsis-${i}`} className="bc-pagination__ellipsis">…</span>
          ) : (
            <button
              key={p}
              className={`bc-pagination__btn ${p === currentPage ? "is-active" : ""}`}
              onClick={() => onPageChange(p)}
              aria-current={p === currentPage ? "page" : undefined}
            >
              {p}
            </button>
          )
        )}

        <button
          className="bc-pagination__btn"
          disabled={currentPage === totalPages || totalPages === 0}
          onClick={() => onPageChange(currentPage + 1)}
          aria-label="Next page"
        >
          <ChevronRightIcon size={16} />
        </button>
      </div>
    </div>
  );
};

/* ────────── Toast Notifications ────────── */
const SuccessToast = ({ message, onClose }) => (
  <div className="dept-toast dept-toast--success" role="alert">
    <span className="dept-toast__icon"><CheckCircleIcon size={20} /></span>
    <span className="dept-toast__msg">{message}</span>
    <button className="dept-toast__close" onClick={onClose} type="button" aria-label="Close">
      <CloseIcon size={15} />
    </button>
  </div>
);

const ErrorToast = ({ message, onClose }) => (
  <div className="dept-toast dept-toast--error" role="alert">
    <span className="dept-toast__icon"><XCircleIcon size={20} /></span>
    <span className="dept-toast__msg">{message}</span>
    <button className="dept-toast__close" onClick={onClose} type="button" aria-label="Close">
      <CloseIcon size={15} />
    </button>
  </div>
);

/* ────────── Main Component ────────── */
const Specialization = () => {
  const [loading, setLoading] = useState(false);
  // Holds ALL specializations fetched from the API (we fetch a large page so client-side paging works)
  const [specialization, setCategories] = useState([]);
  const [filterText, setFilterText] = useState("");
  const [statusFilter, setStatusFilter] = useState([]);

  const [name, setName] = useState("");
  const [status, setStatus] = useState("Active");
  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState(null);

  const [messagealert, setMessageAlert] = useState("");
  const [messageType, setMessageType] = useState("");

  const [isEditMode, setIsEditMode] = useState(false);
  const [editSlug, setEditSlug] = useState(null);

  const [showModal, setShowModal] = useState(false);
  const [togglingSlug, setTogglingSlug] = useState(null);

  const [selectedRows, setSelectedRows] = useState([]);
  const [toggleCleared, setToggleCleared] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  // ── Custom confirm dialog state ──
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "",
    message: "",
    confirmLabel: "Confirm",
    confirmVariant: "danger",
    onConfirm: null,
  });

  const showConfirm = useCallback(({ title, message, confirmLabel, confirmVariant = "danger", onConfirm }) => {
    setConfirmDialog({ open: true, title, message, confirmLabel, confirmVariant, onConfirm });
  }, []);

  const closeConfirm = useCallback(() => {
    setConfirmDialog((p) => ({ ...p, open: false, onConfirm: null }));
  }, []);

  const token = localStorage.getItem("authToken");

  /* ── Image preview handler ── */
  useEffect(() => {
    if (!image) return;
    const objectUrl = URL.createObjectURL(image);
    setPreview(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [image]);

  /* ── Fetch Specializations ──
     We fetch a large page (per_page=1000) once so client-side pagination, filtering
     and search all work consistently against the full dataset. This keeps the existing
     API contract intact (the API call & params still work the same way).
  */
  const fetchCategories = () => {
    setLoading(true);
    fetch(`${API_BASE_URL}api/specialization?page=1&per_page=1000`, {
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
      },
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status) {
          setCategories(data.data || []);
        }
      })
      .catch((err) => console.error("Error fetching specializations:", err))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  /* ── Filtered list (search + status) ── */
  const filteredCategories = useMemo(() => {
    let result = specialization;
    if (filterText) {
      const ft = filterText.toLowerCase();
      result = result.filter(
        (d) =>
          d.name?.toLowerCase().includes(ft) ||
          d.slug?.toLowerCase().includes(ft)
      );
    }
    if (statusFilter.length > 0) {
      result = result.filter((d) => statusFilter.includes(d.status));
    }
    return result;
  }, [filterText, statusFilter, specialization]);

  /* ── Reset to first page when filters change ── */
  useEffect(() => {
    setCurrentPage(1);
  }, [filterText, statusFilter]);

  /* ── If currentPage becomes out of bounds (e.g. after delete), clamp it ── */
  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(filteredCategories.length / perPage));
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [filteredCategories.length, perPage, currentPage]);

  const flashMessage = (msg, type = "success") => {
    setMessageAlert(msg);
    setMessageType(type);
    setTimeout(() => {
      setMessageAlert("");
      setMessageType("");
    }, 4500);
  };

  /* ── Toggle Status (iPhone toggle — no confirm, direct API call) ── */
  const handleStatusChange = (slug, currentStatus) => {
    setTogglingSlug(slug);
    const newStatus = currentStatus === "Active" ? "InActive" : "Active";

    fetch(`${API_BASE_URL}api/specialization/status/${slug}`, {
      method: "GET",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status) {
          flashMessage(`Status updated to ${newStatus} successfully!`, "success");
          fetchCategories();
        } else {
          flashMessage("Failed to update status.", "danger");
        }
      })
      .catch(() => flashMessage("Error while updating status.", "danger"))
      .finally(() => setTogglingSlug(null));
  };

  /* ── Save / Update ── */
  const handleSaveSpecialization = (e) => {
    e.preventDefault();

    const formData = new FormData();
    formData.append("name", name);
    formData.append("is_home", 1);
    formData.append("status", status);
    if (image) formData.append("image", image);

    const url = isEditMode
      ? `${API_BASE_URL}api/specialization/${editSlug}`
      : `${API_BASE_URL}api/specialization`;

    fetch(url, {
      method: "POST",
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status === true) {
          setShowModal(false);
          flashMessage(
            isEditMode
              ? "Specialization updated successfully!"
              : "Specialization added successfully!",
            "success"
          );
          setName("");
          setStatus("Active");
          setImage(null);
          setPreview(null);
          setIsEditMode(false);
          setEditSlug(null);
          fetchCategories();
        } else {
          flashMessage("Failed to save specialization.", "danger");
        }
      })
      .catch(() => flashMessage("Something went wrong!", "danger"));
  };

  /* ── Edit ── */
  const handleEdit = (row) => {
    setIsEditMode(true);
    setEditSlug(row.slug);
    setName(row.name);
    setStatus(row.status);
    setPreview(
      row.image
        ? `${API_BASE_URL}public/specialization/${row.image}`
        : `${API_BASE_URL}public/noimage.jpg`
    );
    setImage(null);
    setShowModal(true);
  };

  /* ── Delete single (now using custom confirm dialog) ── */
  const handleDelete = (slug) => {
    showConfirm({
      title: "Delete Specialization",
      message: "Are you sure you want to delete this specialization? This action cannot be undone.",
      confirmLabel: "Yes, Delete",
      confirmVariant: "danger",
      onConfirm: () => {
        closeConfirm();
        fetch(`${API_BASE_URL}api/specialization/destroy/${slug}`, {
          method: "GET",
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        })
          .then((res) => res.json())
          .then((data) => {
            if (data.status) {
              flashMessage("Specialization deleted successfully!", "success");
              fetchCategories();
            } else {
              flashMessage("Failed to delete specialization.", "danger");
            }
          })
          .catch(() => flashMessage("Error deleting specialization.", "danger"));
      },
    });
  };

  /* ── Bulk delete (now using custom confirm dialog) ── */
  const handleBulkDelete = () => {
    if (selectedRows.length === 0) return;
    const count = selectedRows.length;
    showConfirm({
      title: "Delete Selected",
      message: `Are you sure you want to delete ${count} selected specialization${count > 1 ? "s" : ""}? This action cannot be undone.`,
      confirmLabel: `Delete ${count} Item${count > 1 ? "s" : ""}`,
      confirmVariant: "danger",
      onConfirm: () => {
        closeConfirm();
        Promise.all(
          selectedRows.map((row) =>
            fetch(`${API_BASE_URL}api/specialization/destroy/${row.slug}`, {
              method: "GET",
              headers: {
                Accept: "application/json",
                Authorization: `Bearer ${token}`,
              },
            }).then((res) => res.json())
          )
        )
          .then(() => {
            flashMessage(
              `${count} specialization${count > 1 ? "s" : ""} deleted successfully!`,
              "success"
            );
            setSelectedRows([]);
            setToggleCleared(!toggleCleared);
            fetchCategories();
          })
          .catch(() => flashMessage("Error deleting specializations!", "danger"));
      },
    });
  };

  /* ── Open Add modal ── */
  const openAddModal = () => {
    setIsEditMode(false);
    setEditSlug(null);
    setName("");
    setStatus("Active");
    setImage(null);
    setPreview(null);
    setShowModal(true);
  };

  /* ── Pagination helpers ── */
  const totalRows = filteredCategories.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / perPage));

  const pagedData = useMemo(() => {
    const start = (currentPage - 1) * perPage;
    return filteredCategories.slice(start, start + perPage);
  }, [filteredCategories, currentPage, perPage]);

  /* ────────── Table Columns ────────── */
  const columns = useMemo(
    () => [
      {
        name: "Image",
        cell: (row) => (
          <div className="dept-img-wrap">
            <img
              src={
                row.image
                  ? `${API_BASE_URL}public/specialization/${row.image}`
                  : `${API_BASE_URL}public/noimage.jpg`
              }
              alt={row.name}
              className="dept-img"
            />
          </div>
        ),
        width: "80px",
        ignoreRowClick: true,
      },
      {
        name: "Name",
        selector: (row) => row.name,
        cell: (row) => <span className="bc-cell-name">{row.name}</span>,
        sortable: true,
        grow: 2,
      },
      {
        name: "Slug",
        selector: (row) => row.slug,
        cell: (row) => <span className="bc-slug-pill">{row.slug}</span>,
        sortable: true,
        grow: 2,
      },
      {
        name: "Status",
        selector: (row) => row.status,
        cell: (row) => (
          <ToggleSwitch
            checked={row.status === "Active"}
            disabled={togglingSlug === row.slug}
            onChange={() => handleStatusChange(row.slug, row.status)}
          />
        ),
        sortable: true,
        width: "100px",
        ignoreRowClick: true,
      },
      {
        name: "Created",
        selector: (row) => row.created_at,
        cell: (row) => (
          <span className="bc-cell-muted">
            {row.created_at
              ? new Date(row.created_at).toLocaleDateString("en-IN", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                })
              : "—"}
          </span>
        ),
        sortable: true,
        width: "140px",
      },
      {
        name: "Actions",
        cell: (row) => (
          <div className="bc-row-actions">
            <button
              className="bc-ico-btn edit"
              onClick={() => handleEdit(row)}
              title="Edit"
            >
              <EditIcon size={15} />
            </button>
            <button
              className="bc-ico-btn del"
              onClick={() => handleDelete(row.slug)}
              title="Delete"
            >
              <TrashIcon size={15} />
            </button>
          </div>
        ),
        width: "110px",
        right: true,
        ignoreRowClick: true,
        allowOverflow: true,
        button: true,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [togglingSlug]
  );

  /* ────────── DataTable Custom Styles ────────── */
  const tableStyles = {
    table: { style: { background: "transparent" } },
    headRow: {
      style: {
        background: "#f0fbf6",
        borderTopLeftRadius: "12px",
        borderTopRightRadius: "12px",
        borderBottom: "1px solid #d6efe2",
        minHeight: "52px",
      },
    },
    headCells: {
      style: {
        fontSize: "11.5px",
        fontWeight: 700,
        letterSpacing: "0.07em",
        textTransform: "uppercase",
        color: "#5b6b66",
        paddingLeft: "16px",
        paddingRight: "16px",
      },
    },
    rows: {
      style: {
        minHeight: "72px",
        fontSize: "14px",
        color: "#1f2a37",
        borderBottom: "1px solid #eef4f1 !important",
        transition: "background 0.18s ease",
      },
      highlightOnHoverStyle: {
        background: "#f6fcf9",
        borderBottomColor: "#e3f0e9",
        outline: "none",
      },
      stripedStyle: { background: "#fafefb" },
    },
    cells: {
      style: { paddingLeft: "16px", paddingRight: "16px" },
    },
    pagination: { style: { display: "none" } },
  };

  /* ────────── Render ────────── */
  return (
    <>
      <div id="layout-wrapper">
        <Header />

        {loading && (
          <div className="bc-overlay">
            <div className="bc-spinner" />
          </div>
        )}

        <div className="main-content">
          <div className="page-content">
            <div className="bc-shell">

              {/* ── Page Header ── */}
              <div className="bc-head">
                <div>
                  <h2>Specializations</h2>
                  <p>Manage and organise all your specializations in one place.</p>
                </div>
                <button type="button" className="bc-add-btn" onClick={openAddModal}>
                  <PlusIcon size={18} />
                  Add Specialization
                </button>
              </div>

              {/* ── Toast Alert (page top) ── */}
              {messagealert && messageType === "success" && (
                <SuccessToast message={messagealert} onClose={() => setMessageAlert("")} />
              )}
              {messagealert && messageType === "danger" && (
                <ErrorToast message={messagealert} onClose={() => setMessageAlert("")} />
              )}

              {/* ── Main Card ── */}
              <div className="bc-card">
                {/* Toolbar */}
                <div className="bc-card-top">
                  <div className="bc-card-top-left">
                    <div className="bc-search-box">
                      <SearchIcon size={17} />
                      <input
                        type="text"
                        placeholder="Search by name or slug…"
                        value={filterText}
                        onChange={(e) => setFilterText(e.target.value)}
                      />
                    </div>
                    <MultiSelect
                      options={["Active", "InActive"]}
                      value={statusFilter}
                      onChange={setStatusFilter}
                      placeholder="Filter by status…"
                    />
                  </div>
                  <div className="bc-total-badge">
                    <StarIcon size={16} />
                    Total:&nbsp;<b>{filteredCategories.length}</b>
                  </div>
                </div>

                {/* Bulk action bar */}
                {selectedRows.length > 0 && (
                  <div className="bc-bulk-bar">
                    <div className="bc-bulk-count">
                      <span className="bc-num-badge">{selectedRows.length}</span>
                      specialization{selectedRows.length > 1 ? "s" : ""} selected
                    </div>
                    <div className="bc-bulk-actions">
                      <button
                        className="bc-bulk-btn ghost"
                        type="button"
                        onClick={() => { setSelectedRows([]); setToggleCleared(!toggleCleared); }}
                      >
                        Clear
                      </button>
                      <button
                        className="bc-bulk-btn danger"
                        type="button"
                        onClick={handleBulkDelete}
                      >
                        <TrashIcon size={14} />
                        Delete Selected
                      </button>
                    </div>
                  </div>
                )}

                {/* Data Table */}
                <DataTable
                  columns={columns}
                  data={pagedData}
                  customStyles={tableStyles}
                  highlightOnHover
                  responsive
                  striped
                  selectableRows
                  onSelectedRowsChange={({ selectedRows }) => setSelectedRows(selectedRows)}
                  clearSelectedRows={toggleCleared}
                  noDataComponent={
                    <div className="bc-empty">
                      <StarIcon size={40} />
                      <p>No specializations found</p>
                      <span>Try adjusting your search or add a new specialization</span>
                    </div>
                  }
                />

                {/* Custom Pagination */}
                {filteredCategories.length > 0 && (
                  <CustomPagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                    perPage={perPage}
                    totalRows={totalRows}
                    onPerPageChange={(n) => { setPerPage(n); setCurrentPage(1); }}
                  />
                )}
              </div>
            </div>
          </div>

          {/* ────────── Add / Edit Modal ────────── */}
          {showModal && (
            <div
              className="bc-modal-overlay"
              onClick={(e) => { if (e.target === e.currentTarget) setShowModal(false); }}
              aria-modal="true"
              role="dialog"
              aria-labelledby="specModalTitle"
            >
              <div className="bc-modal">
                {/* Modal Header */}
                <div className="bc-modal__header">
                  <div className="bc-modal__header-left">
                    <div className="bc-modal__icon">
                      <StarIcon size={20} />
                    </div>
                    <div>
                      <p className="bc-modal__title" id="specModalTitle">
                        {isEditMode ? "Edit Specialization" : "Add New Specialization"}
                      </p>
                      <p className="bc-modal__subtitle">
                        {isEditMode
                          ? "Update the details for this specialization"
                          : "Fill in the details to create a new specialization"}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="bc-modal__close"
                    onClick={() => setShowModal(false)}
                    aria-label="Close modal"
                  >
                    <CloseIcon size={16} />
                  </button>
                </div>

                {/* Modal Body */}
                <form onSubmit={handleSaveSpecialization}>
                  <div className="bc-modal__body">

                    {/* Specialization Name */}
                    <div className="bc-form-group">
                      <label className="bc-form-label" htmlFor="specName">
                        Specialization Name <span>*</span>
                      </label>
                      <input
                        type="text"
                        id="specName"
                        className="bc-form-control"
                        placeholder="e.g. Cardiology"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                        autoFocus
                      />
                    </div>

                    {/* Image Upload */}
                    <div className="bc-form-group">
                      <label className="bc-form-label">Specialization Image</label>
                      {!preview ? (
                        <div className="dept-upload-area">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => setImage(e.target.files[0])}
                          />
                          <div className="dept-upload-icon">
                            <ImageIcon size={32} />
                          </div>
                          <div className="dept-upload-label">
                            <strong>Click to upload</strong> or drag & drop
                            <br />
                            <span style={{ fontSize: "11px", color: "#b0bcb6" }}>PNG, JPG, WEBP up to 5MB</span>
                          </div>
                        </div>
                      ) : (
                        <div className="dept-preview-wrap">
                          <img src={preview} alt="Preview" className="dept-preview-img" />
                          <div className="dept-preview-info">
                            <div className="dept-preview-name">
                              {image ? image.name : "Current image"}
                            </div>
                            <div className="dept-preview-hint">
                              {image ? `${(image.size / 1024).toFixed(1)} KB` : "Upload a new image to replace"}
                            </div>
                          </div>
                          <button
                            type="button"
                            className="dept-preview-remove"
                            onClick={() => { setImage(null); setPreview(null); }}
                            title="Remove image"
                          >
                            <CloseIcon size={14} />
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Status Toggle */}
                    <div className="bc-form-group">
                      <label className="bc-form-label">Status</label>
                      <div className="dept-status-row">
                        <div>
                          <div className="dept-status-row-label">Active Status</div>
                          <div className="dept-status-row-hint">
                            Toggle to set this specialization as active or inactive
                          </div>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                          <span className={`dept-status-indicator ${status === "Active" ? "active" : "inactive"}`}>
                            {status === "Active" ? "Active" : "Inactive"}
                          </span>
                          <ToggleSwitch
                            checked={status === "Active"}
                            onChange={() => setStatus(status === "Active" ? "InActive" : "Active")}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Modal Footer */}
                  <div className="bc-modal__footer">
                    <button
                      type="button"
                      className="bc-btn-cancel"
                      onClick={() => setShowModal(false)}
                    >
                      Cancel
                    </button>
                    <button type="submit" className="bc-btn-submit">
                      {isEditMode ? (
                        <>
                          <EditIcon size={15} />
                          Update Specialization
                        </>
                      ) : (
                        <>
                          <PlusIcon size={15} />
                          Add Specialization
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* ────────── Custom Confirm Dialog (replaces window.confirm) ────────── */}
          <ConfirmDialog
            open={confirmDialog.open}
            title={confirmDialog.title}
            message={confirmDialog.message}
            confirmLabel={confirmDialog.confirmLabel}
            confirmVariant={confirmDialog.confirmVariant}
            onConfirm={confirmDialog.onConfirm}
            onCancel={closeConfirm}
          />

          <Footer />
        </div>
      </div>
    </>
  );
};

export default Specialization;